/**
 * sizerUi — shared visual kit for the three pricing sizers (RTL/GUC, DSCR,
 * DSCR Quick Entry). Encodes the sizer design system: outlined form card,
 * top-bordered sections, pill/chip selectors, label-above fields with mono
 * numeric inputs + $/% affixes, the loan-breakdown bar, collapsible repeaters,
 * the sticky results card (status pill, empty state, hero metric, KV rows,
 * badges, coupon ladder), and the primary/secondary action bar.
 *
 * The RFF-bound inputs (RffText/RffMoney/RffPercent/RffNum/RffSelect/RffChips)
 * are thin <Field> wrappers around the plain styled inputs, so react-final-form
 * value + validation semantics are preserved (money stored as numbers, percents
 * stored as decimals when `factor={100}`). Screens compose these — no MUI
 * TextField/Select here, so the look is fully controlled.
 */
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { Field } from "react-final-form";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CodeRoundedIcon from "@mui/icons-material/CodeRounded";
import DataObjectRounded from "@mui/icons-material/DataObjectRounded";
import JsonView from "react18-json-view";
import "react18-json-view/src/style.css";

import Flx from "../../components/layout/Flx";
import { teal as PRIMARY } from "../../theme/colors";
import {
  CASH_OUT_REFI,
  DELAYED_PURCHASE_FIELD,
  LEGACY_DELAYED_PURCHASE,
  canBeDelayedPurchase,
} from "../../utils/loans/delayedPurchase";
import Txt from "../../components/typography/Txt";
import { usePermissionsHook } from "../../hooks/usePermissionsHook";

/* ============================================================ Tokens */
export const SZ = {
  // The accent scale is the PROJECT PRIMARY (theme/colors `teal`), not a
  // sizer-local teal — so every sizer button, chip and link follows the
  // partner brand (Eastview teal, Lendmarq blue) with no per-repo edit.
  teal700: PRIMARY[700],
  teal600: PRIMARY[600],
  teal500: PRIMARY[500],
  teal50: PRIMARY[50],
  teal100: PRIMARY[150],
  ink900: "#101c22",
  ink700: "#33444d",
  ink600: "#4a5b64",
  ink500: "#5f7078",
  ink400: "#8a99a0",
  line: "#e2e8ea",
  lineStrong: "#cbd6d9",
  bg: "#f6f8f9",
  card: "#ffffff",
  good: "#0e8a5f",
  goodBg: "#e8f6f0",
  warn: "#a05a0c",
  warnBg: "#fbf1e2",
  bad: "#b3352e",
  badBg: "#fbeceb",
  // The results rail’s dark hero. Surface shades of the existing ink — the
  // rate is the one figure on the page that should read as the answer, and a
  // dark card is how it stops competing with five white ones beneath it.
  heroBg: "#101c33",
  heroPanel: "#18263f",
  heroChip: "#22334f",
  heroMuted: "#9aa6bf",
  heroChipInk: "#b7c1d6",
  // The one accent that has to read as a WARNING on the dark hero — SZ.warn is
  // tuned for white cards and goes muddy at 12px on #101c33. Used for the price
  // cap marker and any at-the-edge figure inside the hero.
  heroWarn: "#f0b45c",
  scoreExp: "#5b8def",
  radius: "10px",
  radiusSm: "8px",
  shadow: "0 1px 2px rgba(16,28,34,.05), 0 4px 16px rgba(16,28,34,.05)",
  mono: "'IBM Plex Mono', ui-monospace, 'SFMono-Regular', Menlo, monospace",
};

/* ============================================================ Dev tools */
// Internal-only affordances shared by all three sizers: the live-values spy and
// the "Fill demo scenario" button.
/* ============================================================ Page metrics */
/**
 * The sizer page box, as ONE pair of numbers.
 *
 * `SizerShell`'s content column and `FixedActionBar` are two separately
 * positioned boxes that have to read as one column — the bar is `position:
 * fixed`, so nothing makes it inherit the shell's geometry. When each carried
 * its own literals they drifted (the bar defaulted to 1500 against the shell's
 * 1400) and the buttons sat 50px outside the cards on either side at wide
 * widths. Both now measure from here; changing the page width means changing
 * these and nothing else.
 */
export const SIZER_MAX_W = 1800;
export const SIZER_GUTTER = { xs: 2, md: 3.5 };

/**
 * ResultStack — how a results COLUMN arranges its cards.
 *
 * The two results columns (`DscrResultsColumn`, `RtlResultsColumn`) are stacks
 * of cards built for a ~420px rail, where one column is the only sensible
 * arrangement. Given real width — a scenario's priced-result panel on Priced
 * Scenarios, which has the whole page — that same stack becomes a very long
 * scroll beside empty space.
 *
 * `masonry` flows them into CSS multi-columns instead, the same idiom the Loan
 * Drilldown Loan Info redesign tab uses: a column WIDTH, not a count, so the
 * browser fits as many as the space allows. That is what makes this safe to put
 * on a shared component — at rail width `columnWidth` resolves to exactly one
 * column, so the sizers render byte-for-byte what they render today and only the
 * wide surface changes.
 *
 * Children carry their own bottom margin because a column box has no row-gap.
 */
export const ResultStack = ({
  masonry,
  gap = 1.75,
  minColumnWidth = 420,
  children,
}) =>
  masonry ? (
    <Box
      sx={{
        columnWidth: `${minColumnWidth}px`,
        columnGap: "20px",
        "& > *": {
          breakInside: "avoid",
          WebkitColumnBreakInside: "avoid",
          width: "100%",
          mb: gap,
        },
      }}
    >
      {children}
    </Box>
  ) : (
    <Flx column g={gap}>
      {children}
    </Flx>
  );

export const SIZER_DEV_BUILD = process.env.NODE_ENV === "development";

/* ============================================================ Helpers */
export const numOnly = (v) =>
  parseFloat(String(v ?? "").replace(/[^0-9.-]/g, "")) || 0;
export const fmtThousands = (v) =>
  v == null || v === "" || isNaN(Number(v))
    ? ""
    : Number(v).toLocaleString("en-US");
export const fmt$ = (v) => "$" + Math.round(numOnly(v)).toLocaleString("en-US");

/* ============================================================ Shell + layout */

/** Nearest ancestor that actually scrolls, or null for the document. */
const scrollParentOf = (el) => {
  let node = el?.parentElement;
  while (node) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") return node;
    node = node.parentElement;
  }
  return null;
};

/**
 * Put the top of the page back in view — called after a run, so the answer is
 * read from its first line rather than from wherever the Calculate button
 * happened to be.
 *
 * WHICH element scrolls is not knowable here: `SizerShell` deliberately opens no
 * scroll container of its own (see below), and the surface it mounts in differs
 * — SizersLayout's main column under `/sizers/*`, the document on the
 * standalone routes. So it walks up from the shell to whichever ancestor
 * actually scrolls and scrolls that, falling back to the window. Same technique
 * as the pricing screens' `SectionRail` scroll-spy.
 *
 * Pass `SizerShell`'s `rootRef` node.
 */
export const scrollSizerToTop = (node, { instant = false } = {}) => {
  if (!node) return;
  const root = scrollParentOf(node);
  (root || window).scrollTo({ top: 0, behavior: instant ? "auto" : "smooth" });
};

// `headerAction` renders flush right on the title row — for a page-level control
// that isn't part of the form (e.g. the admin-only "Fill demo scenario" button).
// `rootRef` exposes the outer element for `scrollSizerToTop`.
// `tabs` renders on its own row UNDER the title block — for a switcher between
// modes of the same screen (DSCR Full Entry / Quick Entry). It sits below the
// title rather than beside it because it belongs to the page, not to the header
// row: what it changes is everything under it.
export const SizerShell = ({
  title,
  subtitle,
  headerAction,
  // The way BACK out of a sub-view, above the title rather than beside it: a
  // "← All scenarios" sitting in the header-action slot reads as an action on
  // the thing being shown. It is navigation, so it belongs where a breadcrumb
  // would be — top left, above the name of where you are.
  backAction,
  tabs,
  rootRef,
  // Replaces the whole title row (and backAction) — the scenario workspace
  // brings its own sticky header (sizersShared/workspace).
  header,
  // The 128px bottom padding clears FixedActionBar; a page without one passes
  // a smaller value.
  pb = "128px",
  children,
}) => (
  // NOT a scroll container. These pages mount under SizersLayout, whose main
  // column owns the scroll — a second scroller here nests one inside the other
  // and you get two scrollbars (the same reason AdminScreenShell doesn't open
  // one). `minHeight` keeps the tinted background running to the fold.
  <Box ref={rootRef} sx={{ minHeight: "100%", bgcolor: SZ.bg }}>
    {/* The bottom padding clears the fixed action bar the RTL sizer pins to the
        window — without it the last card ends underneath the buttons. */}
    <Box
      sx={{
        maxWidth: SIZER_MAX_W,
        mx: "auto",
        px: SIZER_GUTTER,
        pt: header ? 0 : 3.5,
        pb,
      }}
    >
      {header}
      {!header && backAction && <Box sx={{ mb: 1.5 }}>{backAction}</Box>}
      {/* Title only — the sizer pages carry no header glyph. */}
      {!header && (
        <Flx ac g={1.5} sx={{ mb: 2.75, justifyContent: "space-between" }}>
          <Box>
            <Txt
              sx={{
                fontSize: 21,
                fontWeight: 700,
                letterSpacing: "-0.01em",
                color: SZ.ink900,
                lineHeight: 1.15,
              }}
            >
              {title}
            </Txt>
            {subtitle && (
              <Txt sx={{ fontSize: 13, color: SZ.ink500, mt: "3px" }}>
                {subtitle}
              </Txt>
            )}
          </Box>
          {headerAction}
        </Flx>
      )}
      {tabs && <Box sx={{ mt: -1, mb: 2.75 }}>{tabs}</Box>}
      {children}
    </Box>
  </Box>
);

// Two-column workbench: form (fluid) + sticky results rail (420px). The rail
// carries the whole priced answer — rate hero, leverage table, classification,
// market — so it is sized to hold a 3-column table, not a KV list.
export const Workbench = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "minmax(0,1fr) 420px" },
      gap: 3,
      alignItems: "start",
    }}
  >
    {children}
  </Box>
);

// The sticky offset reads `--sizer-sticky-top` so a page with its own pinned
// header (the scenario workspace) can push the rail below it.
//
// The rail SCROLLS ON ITS OWN (owner 2026-10-19): it is capped at the space left
// under the pinned strips (the 64px app header + `--sizer-sticky-top`) and
// scrolls inside that, so a long results column can be read without moving the
// form, and the form scrolls past without dragging the results with it.
// `overscrollBehavior: contain` keeps a scroll that hits the rail's end from
// spilling into the page.
const APP_HEADER_H = 64;
// Scrolled content FADES at the edge it is scrolled past (a 16px mask), rather
// than being cut off hard under the pinned bar; an edge with nothing beyond it
// stays crisp, so an unscrolled rail shows its first card whole.
const RAIL_FADE = 16;
export const ResultsCol = ({ children }) => {
  const ref = useRef(null);
  const [edges, setEdges] = useState({ top: false, bottom: false });
  /* Edges are read ONLY from scroll / resize events — never during render or
     from a callback ref. A callback ref re-fires on every render, and reading
     a hidden rail (Results mode sets display:none on the Inputs layout) while
     it toggles back in fed setState → render → ref → setState in a loop
     ("Maximum update depth exceeded"). */
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const read = () => {
      // A hidden rail has no box — leave the last answer alone.
      if (!el.clientHeight) return;
      const top = el.scrollTop > 1;
      const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
      setEdges((prev) =>
        prev.top === top && prev.bottom === bottom ? prev : { top, bottom },
      );
    };
    read();
    el.addEventListener("scroll", read, { passive: true });
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(read) : null;
    ro?.observe(el);
    if (el.firstElementChild) ro?.observe(el.firstElementChild);
    return () => {
      el.removeEventListener("scroll", read);
      ro?.disconnect();
    };
  }, []);
  const mask = `linear-gradient(to bottom, ${edges.top ? "transparent" : "#000"} 0, #000 ${RAIL_FADE}px, #000 calc(100% - ${RAIL_FADE}px), ${edges.bottom ? "transparent" : "#000"} 100%)`;
  return (
    <Box
      ref={ref}
      sx={{
        position: { md: "sticky" },
        top: { md: "var(--sizer-sticky-top, 24px)" },
        maxHeight: {
          md: `calc(100vh - ${APP_HEADER_H}px - var(--sizer-sticky-top, 24px) - 12px)`,
        },
        overflowY: { md: "auto" },
        overscrollBehavior: { md: "contain" },
        // Room for the scrollbar and the cards' shadows/stale tag.
        mx: { md: "-6px" },
        px: { md: "6px" },
        pt: { md: "10px" },
        mt: { md: "-10px" },
        pb: { md: 1 },
        scrollbarWidth: "thin",
        WebkitMaskImage: { md: mask },
        maskImage: { md: mask },
      }}
    >
      {children}
    </Box>
  );
};

/**
 * The same two children as `Workbench`, stacked instead of side by side — for a
 * result too wide for a 360px rail (the DSCR ladder + its 9-column sensitivity
 * matrix). Interchangeable with `Workbench`, so a screen can swap layouts on a
 * condition without restructuring its JSX.
 */
export const StackedWorkbench = ({ children }) => (
  <Flx column g={2.5}>
    {children}
  </Flx>
);

export const FormCard = ({ children, sx }) => (
  <Box
    sx={{
      bgcolor: SZ.card,
      border: `1px solid ${SZ.line}`,
      borderRadius: SZ.radius,
      boxShadow: SZ.shadow,
      p: { xs: 2.5, md: "24px 26px 26px" },
      ...sx,
    }}
  >
    {children}
  </Box>
);

/**
 * CollapsibleFormCard — a `FormCard` that folds into a one-line summary once the
 * scenario has been priced.
 *
 * The result IS the page after a run: a 25-rung ladder, a 5×9 matrix and the
 * LLPA trail don't fit beside a 20-field form. So the form collapses to its
 * header, and the inputs stay one click away — reopening never clears anything,
 * so this is a disclosure control, not a mode.
 *
 * `summary` is the caller's list of `{label, value}` chips (the inputs worth
 * seeing without expanding). `collapsed` / `onToggle` are owned by the screen so
 * a successful price can close it.
 *
 * `enabled={false}` renders a PLAIN `FormCard` — no header bar at all. An
 * unpriced form has nothing to collapse away from and nothing to summarize, so
 * the disclosure control would only be chrome the user has to read past; it
 * appears with the answer it exists to make room for.
 */
export const CollapsibleFormCard = ({
  title = "Scenario inputs",
  summary = [],
  collapsed,
  onToggle,
  enabled = true,
  children,
  sx,
}) =>
  !enabled ? (
    <FormCard sx={sx}>{children}</FormCard>
  ) : (
    <Box
      sx={{
        bgcolor: SZ.card,
        border: `1px solid ${SZ.line}`,
        borderRadius: SZ.radius,
        boxShadow: SZ.shadow,
        // NOT overflow:hidden — an overflow-clipping ancestor pins a sticky child
        // (the action bar) to this card instead of the viewport. The header
        // rounds its own corners instead.
        ...sx,
      }}
    >
      <Flx
        jb
        ac
        g={2}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle?.();
          }
        }}
        sx={{
          p: "13px 20px",
          cursor: "pointer",
          userSelect: "none",
          background: collapsed ? "#fafcfc" : SZ.card,
          // Collapsed the header IS the card, so it takes all four corners;
          // expanded it takes the top two and the body squares off beneath it.
          borderRadius: collapsed ? SZ.radius : `${SZ.radius} ${SZ.radius} 0 0`,
          borderBottom: collapsed ? "none" : `1px solid ${SZ.line}`,
          flexWrap: "wrap",
          "&:hover": { background: "#f3f8f9" },
        }}
      >
        <Flx ac g={1.25} sx={{ minWidth: 0, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", color: SZ.ink500 }}>
            <Chevron open={!collapsed} />
          </Box>
          <Txt sx={{ fontSize: 13.5, fontWeight: 700, color: SZ.ink900 }}>
            {title}
          </Txt>
          {collapsed &&
            summary
              .filter((s) => s && s.value !== "" && s.value != null)
              .map((s, i) => (
                <Flx
                  key={`${s.label}-${i}`}
                  ac
                  g={0.5}
                  sx={{ flexWrap: "nowrap" }}
                >
                  <Txt sx={{ fontSize: 11.5, color: SZ.ink400 }}>{s.label}</Txt>
                  <Txt
                    sx={{
                      fontFamily: SZ.mono,
                      fontVariantNumeric: "tabular-nums",
                      fontSize: 12,
                      fontWeight: 600,
                      color: SZ.ink700,
                    }}
                  >
                    {s.value}
                  </Txt>
                </Flx>
              ))}
        </Flx>
        <Txt
          sx={{
            fontSize: 12.5,
            fontWeight: 600,
            color: SZ.teal700,
            whiteSpace: "nowrap",
          }}
        >
          {collapsed ? "Edit inputs" : "Hide inputs"}
        </Txt>
      </Flx>
      {!collapsed && (
        <Box sx={{ p: { xs: 2.5, md: "24px 26px 26px" } }}>{children}</Box>
      )}
    </Box>
  );

// Top-bordered section with a title + optional subtitle and right-aligned action.
export const Section = ({ title, subtitle, action, first, children }) => (
  <Box
    sx={{
      mt: first ? 0 : 4,
      pt: first ? 0 : 4,
      borderTop: first ? "none" : `1px solid ${SZ.line}`,
    }}
  >
    {/* A title with a subtitle under it is ONE block — the subtitle owns the
        gap to the fields, so the title carries no bottom margin of its own or
        the two read as separate things. Without a subtitle the title takes the
        full gap itself. */}
    <Flx jb ac g={2} sx={{ mb: subtitle ? 0 : 2.75, flexWrap: "wrap" }}>
      <Txt
        sx={{
          fontSize: 16.5,
          fontWeight: 700,
          letterSpacing: "-0.01em",
          color: SZ.ink900,
        }}
      >
        {title}
      </Txt>
      {action}
    </Flx>
    {subtitle && (
      <Txt sx={{ fontSize: 13, color: SZ.ink500, mb: 2.75 }}>{subtitle}</Txt>
    )}
    {children}
  </Box>
);

/* ============================================================ Grid + Field */
// Simple responsive grid. `cols` = column count at md+, collapses to 1 on xs.
// `gap` is "row column". The row gap carries the vertical rhythm between field
// rows — label-above fields are two elements tall, so they need more air than a
// floating-label grid would.
export const Grid = ({ cols = 3, gap = "24px", sx, children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        sm: `repeat(${Math.min(cols, 2)}, 1fr)`,
        md: `repeat(${cols}, 1fr)`,
      },
      gap,
      alignItems: "start",
      ...sx,
    }}
  >
    {children}
  </Box>
);

/**
 * A row of chip-group fields, each sized to its own chips rather than to a
 * grid track — so a conditional field (Delayed purchase?) joins the same row
 * when there is room, and wraps onto the next line only when there isn't.
 */
export const ChoiceRow = ({ sx, children }) => (
  <Box
    sx={{
      display: "flex",
      flexWrap: "wrap",
      columnGap: "40px",
      rowGap: "24px",
      alignItems: "flex-start",
      "& > *": { flex: "0 1 auto", maxWidth: "100%" },
      ...sx,
    }}
  >
    {children}
  </Box>
);

export const Stack = ({ gap = 3.25, sx, children }) => (
  <Flx column g={gap} sx={sx}>
    {children}
  </Flx>
);

/**
 * The label line — a FIXED 22px row above a fixed 42px control.
 *
 * Fixed, because every field on a card shares one anatomy: the moment a label
 * wraps to two lines or a hint pushes in underneath, the control beside it sits
 * at a different height and the row loses its baseline. Anything that would have
 * been a hint belongs in a `tip` on the label instead (see Field2).
 */
export const FieldLabel = ({ children, required, sx }) => (
  <Txt
    component="div"
    sx={{
      height: 22,
      display: "flex",
      alignItems: "center",
      gap: "6px",
      fontSize: 13,
      fontWeight: 500,
      color: SZ.ink500,
      mb: "6px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      ...sx,
    }}
  >
    {children}
    {/* RED, not teal. The asterisk is the one mark on the form that says
        something is WRONG-if-missing, and in the sizer's palette teal is the
        colour of everything that is fine. */}
    {required && (
      <Box component="span" sx={{ color: SZ.bad }}>
        {" "}
        *
      </Box>
    )}
  </Txt>
);

export const FieldHint = ({ children }) => (
  <Txt
    sx={{ fontSize: 11.5, color: SZ.ink400, mt: "5px", fontStyle: "italic" }}
  >
    {children}
  </Txt>
);

/**
 * SizerMuiField — normalizes a portal MUI field (the Places address picker, the
 * multi-select checkbox field) onto the sizer's input look.
 *
 * The sizers otherwise use no MUI TextField/Select at all, so a portal component
 * dropped in arrives with the app theme's taller inputs, grey borders and blue
 * focus ring, and reads as a foreign control. Rather than fork those components,
 * this restyles them in place: the sizer's label typography, a 38px-min outlined
 * control with `SZ.lineStrong` / `SZ.radiusSm`, and the teal focus ring the
 * plain inputs use. Behaviour (Places lookup, chips, validation) is untouched.
 */
export const SizerMuiField = ({ children, sx }) => (
  <Box
    sx={{
      // Label rendered by RffInputWrapper — match FieldLabel exactly.
      ".input-label, label, .MuiFormLabel-root": {
        fontSize: 13,
        fontWeight: 500,
        color: SZ.ink500,
        mb: "6px",
      },
      ".MuiOutlinedInput-root": {
        minHeight: 38,
        borderRadius: SZ.radiusSm,
        background: SZ.card,
        fontSize: 13.5,
        color: SZ.ink900,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: SZ.lineStrong,
          borderWidth: "1px",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#a9bcc1" },
        "&.Mui-focused": {
          boxShadow: "0 0 0 3px rgba(20,160,170,.15)",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: SZ.teal500,
            borderWidth: "1px",
          },
        },
      },
      ".MuiOutlinedInput-input": { fontSize: 13.5, py: "8px" },
      ".MuiInputBase-input::placeholder": { color: SZ.ink400, opacity: 1 },
      ".MuiFormHelperText-root": {
        fontSize: 11.5,
        color: SZ.ink400,
        fontStyle: "italic",
        ml: 0,
        mt: "5px",
      },
      ".MuiChip-root": {
        height: 24,
        fontSize: 12,
        borderRadius: "99px",
        background: SZ.teal50,
        color: SZ.teal700,
        border: `1px solid ${SZ.teal600}`,
      },
      ...sx,
    }}
  >
    {children}
  </Box>
);

// Label-above field wrapper. `span` sets grid column span.
// `span` is RESPONSIVE: the grid only has 1–2 tracks below md, where a raw
// `span 6`/`span 9` would spill into implicit columns and break the row — so a
// spanned field is full width until md and takes its span above.
/**
 * A field cell: label line, control, and nothing else in the flow.
 *
 * `tip` is the preferred home for what used to be `hint` — an info glyph on the
 * label, with the text in a tooltip. A hint UNDER the control is real layout: it
 * makes one cell in a row taller than its neighbours, and the shared baseline
 * the card is built on goes with it. `hint` stays for the few notes that must be
 * read without hovering (a conditional warning, a count mismatch), and those
 * belong on fields that sit on their own row.
 */
export const Field2 = ({ label, required, hint, tip, span, sx, children }) => (
  <Box
    sx={{
      minWidth: 0,
      gridColumn: span ? { xs: "1 / -1", md: `span ${span}` } : undefined,
      ...sx,
    }}
  >
    {label && (
      <FieldLabel required={required}>
        {label}
        {tip && <InfoTip text={tip} />}
      </FieldLabel>
    )}
    {children}
    {hint && <FieldHint>{hint}</FieldHint>}
  </Box>
);

/** The info glyph that carries a field's note out of the layout flow. */
export const InfoTip = ({ text }) => (
  <Tooltip title={text} placement="top" arrow>
    <Box
      component="span"
      tabIndex={0}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        color: SZ.ink400,
        cursor: "help",
        flexShrink: 0,
        "&:hover, &:focus-visible": { color: SZ.teal700 },
      }}
    >
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4M12 8h.01" />
      </svg>
    </Box>
  </Tooltip>
);

/* ============================================================ Inputs (plain) */
const baseInputSx = {
  width: "100%",
  height: 42,
  padding: "0 12px",
  border: `1px solid ${SZ.lineStrong}`,
  borderRadius: SZ.radiusSm,
  background: SZ.card,
  fontSize: 13.5,
  color: SZ.ink900,
  fontFamily: "inherit",
  outline: "none",
  transition: "border-color .12s, box-shadow .12s",
  appearance: "none",
  "&::placeholder": { color: SZ.ink400 },
  "&:hover": { borderColor: "#a9bcc1" },
  "&:focus": {
    borderColor: SZ.teal500,
    boxShadow: "0 0 0 3px rgba(20,160,170,.15)",
  },
  "&:disabled": {
    background: "#f4f7f8",
    color: SZ.ink400,
    cursor: "not-allowed",
  },
};

const monoSx = { fontFamily: SZ.mono, fontSize: 13 };

// Wraps an <input> with an optional $ prefix / % suffix pill.
const Affix = ({ prefix, suffix, children }) => (
  <Box sx={{ position: "relative" }}>
    {prefix && (
      <Box
        sx={{
          position: "absolute",
          left: 11,
          top: "50%",
          transform: "translateY(-50%)",
          fontSize: 13,
          color: SZ.ink400,
          pointerEvents: "none",
        }}
      >
        {prefix}
      </Box>
    )}
    {children}
    {suffix && (
      <Box
        sx={{
          position: "absolute",
          right: 11,
          top: "50%",
          transform: "translateY(-50%)",
          fontSize: 13,
          color: SZ.ink400,
          pointerEvents: "none",
        }}
      >
        {suffix}
      </Box>
    )}
  </Box>
);

export const TextInput = ({ mono, sx, ...props }) => (
  <Box
    component="input"
    type="text"
    sx={{ ...baseInputSx, ...(mono ? monoSx : {}), ...sx }}
    {...props}
  />
);

export const MoneyInput = ({ value, onChange, onBlur, sx, ...props }) => {
  /* The raw text the user is typing, held ONLY while it cannot survive a round
     trip through the formatter. Two keystrokes could not: emptying the field
     (`numOnly("")` is 0, so a cleared field snapped straight back to $0 and
     could not be edited), and the "." of "1200.50" (stripped the instant it is
     typed, so the next digit lands a place out). Everything else keeps the live
     thousands formatting. */
  const [raw, setRaw] = useState(null);
  const typingDecimal = raw != null && raw.indexOf(".") !== -1;
  const shown = typingDecimal
    ? raw
    : value === 0 || value
      ? fmtThousands(value)
      : "";
  return (
    <Affix prefix="$">
      <Box
        component="input"
        type="text"
        inputMode="decimal"
        value={shown}
        onChange={(e) => {
          const next = e.target.value;
          setRaw(next);
          onChange?.(next.trim() === "" ? "" : numOnly(next));
        }}
        onBlur={(e) => {
          setRaw(null);
          onBlur?.(e);
        }}
        sx={{ ...baseInputSx, ...monoSx, pl: "26px", ...sx }}
        {...props}
      />
    </Affix>
  );
};

// Percent input. `factor` = multiplier between the stored value and the shown
// value (100 → stored decimal 0.9 shows as "90"; 1 → stored 7 shows as "7").
export const PercentInput = ({
  value,
  onChange,
  onBlur,
  factor = 1,
  sx,
  ...props
}) => {
  const n = Number(value) * factor;
  // Round to kill float noise (0.29*100 = 28.9999…) without forcing trailing
  // zeros, so typing "90" doesn't fight a reformatted "90.0".
  const canonical =
    value === "" || value == null || isNaN(n)
      ? ""
      : String(Math.round(n * 1e6) / 1e6);
  /* While the field has focus the RAW text wins. Deriving the display from the
     stored number alone erased any keystroke that does not round trip — the "."
     of "89.5" vanished as soon as it was typed, so the next digit read as
     "895" (8.95x leverage). The stored value still updates on every keystroke;
     this owns what is DISPLAYED, and blur hands the display back. */
  const [raw, setRaw] = useState(null);
  const shown = raw != null ? raw : canonical;
  return (
    <Affix suffix="%">
      <Box
        component="input"
        type="text"
        inputMode="decimal"
        value={shown}
        onChange={(e) => {
          const next = e.target.value;
          setRaw(next);
          onChange?.(next.trim() === "" ? "" : numOnly(next) / factor);
        }}
        onBlur={(e) => {
          setRaw(null);
          onBlur?.(e);
        }}
        sx={{ ...baseInputSx, ...monoSx, pr: "30px", ...sx }}
        {...props}
      />
    </Affix>
  );
};

export const NumInput = ({ value, onChange, onBlur, sx, ...props }) => (
  <Box
    component="input"
    type="text"
    inputMode="numeric"
    value={value === 0 || value ? value : ""}
    onChange={(e) =>
      onChange?.(e.target.value === "" ? "" : numOnly(e.target.value))
    }
    onBlur={onBlur}
    sx={{ ...baseInputSx, ...monoSx, ...sx }}
    {...props}
  />
);

const StepChevron = ({ up }) => (
  <svg
    width="9"
    height="6"
    viewBox="0 0 12 8"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d={up ? "M1 6.5 6 1.5 11 6.5" : "M1 1.5 6 6.5 11 1.5"} />
  </svg>
);

/**
 * A whole-number input with its own stepper.
 *
 * For counts that are TYPICALLY 1–3 and always integers — how many guarantors,
 * how many members — where clicking twice beats typing. The arrows are drawn
 * here rather than taken from `<input type="number">`: the kit's base input sets
 * `appearance: none` (that is what makes every field in the sizer look alike),
 * which strips the native spinner, and the browser's own is a 12px hit target
 * that differs per platform.
 *
 * `min` is a floor on the ARROWS, not a validator — typing is left alone so a
 * field can still be cleared.
 *
 * `commitOnBlur` holds typed text back until blur or Enter. For a count that
 * DOES something on change — adds or removes a card — every intermediate
 * keystroke would otherwise act: typing "12" passes through 1 on the way, and
 * eleven cards would be destroyed and rebuilt. The arrows still act instantly;
 * they are unambiguous.
 */
export const StepperInput = ({
  value,
  onChange,
  onBlur,
  min = 0,
  max,
  step = 1,
  commitOnBlur = false,
  sx,
  ...props
}) => {
  const n = value === "" || value == null ? null : Number(value);
  const [draft, setDraft] = useState(null);
  const commit = () => {
    if (draft == null) return;
    onChange?.(draft.trim() === "" ? "" : numOnly(draft));
    setDraft(null);
  };
  const bump = (dir) => {
    const next = (Number.isFinite(n) ? n : 0) + dir * step;
    if (next < min) return;
    if (max != null && next > max) return;
    setDraft(null);
    onChange?.(next);
  };
  const btnSx = {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: 0,
    // Tinted: the arrows read as a control fixed to the end of the field rather
    // than as two glyphs floating over the text.
    background: "#fafbfc",
    color: SZ.ink400,
    cursor: "pointer",
    padding: 0,
    transition: "background .12s, color .12s",
    "&:hover": { background: SZ.teal50, color: SZ.teal700 },
    "&:active": { background: SZ.teal100 },
  };
  return (
    <Box sx={{ position: "relative" }}>
      <Box
        component="input"
        type="text"
        inputMode="numeric"
        value={draft != null ? draft : n === 0 || n ? n : ""}
        onChange={(e) => {
          const next = e.target.value;
          if (commitOnBlur) return setDraft(next);
          onChange?.(next === "" ? "" : numOnly(next));
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          }
        }}
        onBlur={(e) => {
          commit();
          onBlur?.(e);
        }}
        sx={{ ...baseInputSx, ...monoSx, pr: "40px", ...sx }}
        {...props}
      />
      <Box
        sx={{
          position: "absolute",
          top: 1,
          right: 1,
          bottom: 1,
          width: 30,
          display: "flex",
          flexDirection: "column",
          borderLeft: `1px solid ${SZ.line}`,
          borderRadius: `0 ${SZ.radiusSm} ${SZ.radiusSm} 0`,
          overflow: "hidden",
        }}
      >
        <Box
          component="button"
          type="button"
          aria-label="Increase"
          onClick={() => bump(1)}
          sx={btnSx}
        >
          <StepChevron up />
        </Box>
        <Box
          component="button"
          type="button"
          aria-label="Decrease"
          onClick={() => bump(-1)}
          sx={{ ...btnSx, borderTop: `1px solid ${SZ.line}` }}
        >
          <StepChevron />
        </Box>
      </Box>
    </Box>
  );
};

/**
 * SelectInput — the sizer's dropdown.
 *
 * NOT a native `<select>`. A native select's option list is rendered by the
 * operating system, so none of the sizer's typography, radius, spacing or teal
 * selection state reaches it — you get the OS's own list with its bright blue
 * highlight sitting under a carefully styled control, which is exactly the seam
 * this kit exists to avoid. Since the sizers use no other MUI form control,
 * that mismatch had nowhere to hide.
 *
 * So: a button that keeps the plain-input look, plus a MUI `Menu` for the list.
 * MUI owns the popover mechanics (focus trap, arrow keys, Escape, type-ahead,
 * click-away, scroll containment); this file owns the appearance.
 *
 * `placeholder` renders when the value is UNSET, in the muted ink — an
 * unselected dropdown must not look pre-populated, because that is a lie about
 * what will be posted.
 */
export const SelectInput = ({
  value,
  onChange,
  options = [],
  placeholder,
  disabled,
  sx,
  ...props
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const norm = options.map((o) =>
    typeof o === "object" ? o : { value: o, label: String(o) },
  );
  const selected = norm.find((o) => o.value === value);
  const isUnset = value == null || value === "";

  return (
    <>
      <Box
        component="button"
        // Explicit: a bare <button> inside a <form> defaults to type="submit",
        // which would make opening this dropdown submit the scenario.
        type="button"
        disabled={disabled}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-haspopup="listbox"
        aria-expanded={open}
        sx={{
          ...baseInputSx,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          textAlign: "left",
          cursor: disabled ? "not-allowed" : "pointer",
          color: isUnset ? SZ.ink400 : SZ.ink900,
          // The button is the focus target, so it takes the same ring the text
          // inputs get on :focus.
          "&:focus-visible": {
            borderColor: SZ.teal500,
            boxShadow: "0 0 0 3px rgba(20,160,170,.15)",
          },
          ...(open
            ? {
                borderColor: SZ.teal500,
                boxShadow: "0 0 0 3px rgba(20,160,170,.15)",
              }
            : null),
          ...sx,
        }}
        {...props}
      >
        <Box
          component="span"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {selected ? selected.label : placeholder || ""}
        </Box>
        <Box
          component="span"
          sx={{
            display: "flex",
            color: SZ.ink500,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .15s",
            flexShrink: 0,
          }}
        >
          <Chevron open />
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        // Match the trigger's width so the menu reads as an extension of the
        // control rather than a floating panel.
        slotProps={{
          paper: {
            sx: {
              mt: "5px",
              minWidth: anchorEl?.offsetWidth,
              maxWidth: Math.max(anchorEl?.offsetWidth || 0, 320),
              maxHeight: 320,
              borderRadius: SZ.radiusSm,
              border: `1px solid ${SZ.line}`,
              boxShadow:
                "0 4px 6px -1px rgba(16,28,34,.08), 0 12px 28px -4px rgba(16,28,34,.14)",
              // The kit's own scrollbar — the default is a heavy grey slab.
              "&::-webkit-scrollbar": { width: 8 },
              "&::-webkit-scrollbar-thumb": {
                background: SZ.lineStrong,
                borderRadius: 99,
                border: "2px solid #fff",
              },
            },
          },
          list: { sx: { py: "5px" }, dense: true },
        }}
      >
        {placeholder && (
          <MenuItem disabled sx={{ ...menuItemSx, color: SZ.ink400 }}>
            {placeholder}
          </MenuItem>
        )}
        {norm.map((o) => {
          const isSelected = o.value === value;
          return (
            <MenuItem
              key={String(o.value)}
              selected={isSelected}
              onClick={() => {
                onChange?.(o.value);
                setAnchorEl(null);
              }}
              sx={menuItemSx}
            >
              <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                {o.label}
              </Box>
              {isSelected && (
                <CheckRoundedIcon
                  sx={{ fontSize: 15, color: SZ.teal600, ml: 1 }}
                />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

// Teal selection, not MUI's default primary tint — level 1 of the active-state
// system, the same teal the chips and tabs use.
const menuItemSx = {
  fontSize: 13.5,
  fontFamily: "inherit",
  color: SZ.ink900,
  minHeight: 34,
  px: "12px",
  py: "7px",
  mx: "5px",
  borderRadius: "6px",
  whiteSpace: "normal",
  lineHeight: 1.35,
  "&:hover": { background: SZ.teal50 },
  "&.Mui-selected": {
    background: SZ.teal50,
    color: SZ.teal700,
    fontWeight: 600,
    "&:hover": { background: SZ.teal100 },
  },
  "&.Mui-focusVisible": { background: SZ.teal50 },
};

/**
 * Boolean select.
 *
 * A native `<select>` only ever emits STRINGS, so a boolean field wired straight
 * to `SelectInput` would post `"true"` / `"false"` — and the model's flags
 * (`crossCollateralized`, `dutchInterest`, `floodZone` …) are REAL booleans, so
 * that is a wrong value, not a formatting detail. This maps both directions and
 * keeps `undefined` distinguishable from `false`.
 */
export const BoolSelectInput = ({
  value,
  onChange,
  options = [],
  placeholder,
  sx,
  ...props
}) => (
  <SelectInput
    value={value === true ? "true" : value === false ? "false" : ""}
    onChange={(v) => onChange?.(v === "" ? undefined : v === "true")}
    options={options.map((o) => {
      const val = typeof o === "object" ? o.value : o;
      return {
        value: String(val),
        label: typeof o === "object" ? o.label : String(o),
      };
    })}
    placeholder={placeholder}
    sx={sx}
    {...props}
  />
);

/* ============================================================ Chips */
export const Chip = ({ on, sm, children, onClick, sx }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      border: `1px solid ${on ? SZ.teal600 : SZ.lineStrong}`,
      background: on ? SZ.teal50 : SZ.card,
      color: on ? SZ.teal700 : SZ.ink700,
      borderRadius: "99px",
      padding: sm ? "6px 13px" : "7px 16px",
      fontSize: sm ? 12.5 : 13,
      fontWeight: on ? 600 : 500,
      lineHeight: 1.2,
      cursor: "pointer",
      whiteSpace: "nowrap",
      fontFamily: "inherit",
      transition: "all .12s",
      boxShadow: on ? `inset 0 0 0 1px ${SZ.teal600}` : "none",
      "&:hover": {
        borderColor: SZ.teal500,
        color: SZ.teal700,
        background: SZ.teal50,
      },
      ...sx,
    }}
  >
    {children}
  </Box>
);

// Single-select pill group over `options` [{value,label}] or [string].
export const ChipGroup = ({ value, onChange, options = [], sm, sx }) => (
  <Flx sx={{ flexWrap: "wrap", gap: 1, ...sx }}>
    {options.map((o) => {
      const val = typeof o === "object" ? o.value : o;
      const label = typeof o === "object" ? o.label : o;
      return (
        <Chip
          key={String(val)}
          sm={sm}
          on={value === val}
          onClick={() => onChange?.(val)}
        >
          {label}
        </Chip>
      );
    })}
  </Flx>
);

// Multi-select toggle group: `value` is an array, each chip toggles its own
// value in or out. Nothing on = no filter (callers treat [] as "all").
export const ChipToggleGroup = ({
  value = [],
  onChange,
  options = [],
  sm,
  sx,
}) => (
  <Flx sx={{ flexWrap: "wrap", gap: 1, ...sx }}>
    {options.map((o) => {
      const val = typeof o === "object" ? o.value : o;
      const label = typeof o === "object" ? o.label : o;
      const on = value.includes(val);
      return (
        <Chip
          key={String(val)}
          sm={sm}
          on={on}
          onClick={() =>
            onChange?.(on ? value.filter((v) => v !== val) : [...value, val])
          }
        >
          {label}
        </Chip>
      );
    })}
  </Flx>
);

/* ============================================================ Segmented */

/**
 * SegmentedGroup — the two-option switch for BOOLEAN fields (Yes / No,
 * guarantor, flood zone, dutch interest).
 *
 * A boolean is not the same control as an enumeration: there is no "unset" worth
 * showing, the two options are always visible, and the answer reads at a glance.
 * Chips said "pick one of these tags"; a segmented track says "this is one
 * setting with two states", which is what a boolean is.
 *
 * Built on the house recipe (`segmentedGroupSx` / `level3` in theme/activeStates
 * + theme/colors): `#F5F7F8` track, 3px inset, white active pill at 6px radius
 * with the level-3 shadow. Two deliberate departures from a stock level-3
 * control:
 *   • a `1px solid` hairline on the track — the house rule for this component
 *     (a bare track floats against a white card);
 *   • the active label is teal-700, not ink. Level 3 is neutral because it means
 *     "only the presentation changed"; this switch changes DATA, so it speaks
 *     the same teal the rest of the form's selected states do.
 *
 * `fullWidth` splits the track evenly — right for a grid cell, where a shrink-to-
 * fit switch leaves the column looking half-empty.
 *
 * `fallback` is DISPLAY ONLY: an unanswered field shows that option selected
 * without writing it. The stored value stays `undefined` until the user actually
 * picks one, so "not answered" and "answered No" stay distinguishable on the
 * wire — which matters here, because the backend defaults several of these
 * itself and a volunteered `false` is a claim, not an absence. The visual
 * default only stops the control from rendering as a blank track.
 */
export const SegmentedGroup = ({
  value,
  onChange,
  options = [],
  fullWidth = true,
  fallback,
  sx,
}) => {
  const shown =
    value === undefined || value === null || value === "" ? fallback : value;
  return (
    <Box
      role="group"
      sx={{
        display: fullWidth ? "grid" : "inline-grid",
        gridAutoFlow: "column",
        gridAutoColumns: fullWidth ? "1fr" : "auto",
        gap: "2px",
        p: "3px",
        height: 42,
        background: "#F5F7F8",
        border: `1px solid ${SZ.line}`,
        borderRadius: "9px",
        ...sx,
      }}
    >
      {options.map((o) => {
        const val = typeof o === "object" ? o.value : o;
        const label = typeof o === "object" ? o.label : o;
        const on = shown === val;
        return (
          <Box
            component="button"
            type="button"
            key={String(val)}
            aria-pressed={on}
            onClick={() => onChange?.(val)}
            sx={{
              border: 0,
              borderRadius: "6px",
              px: "13px",
              fontSize: 13,
              fontWeight: on ? 600 : 500,
              fontFamily: "inherit",
              cursor: "pointer",
              transition: "background .12s, color .12s, box-shadow .12s",
              background: on ? "#fff" : "transparent",
              color: on ? SZ.teal700 : SZ.ink500,
              boxShadow: on ? "0 1px 3px rgba(15,23,42,.12)" : "none",
              "&:hover": on
                ? {}
                : { background: "rgba(15,23,42,.04)", color: SZ.ink700 },
            }}
          >
            {label}
          </Box>
        );
      })}
    </Box>
  );
};

/* ============================================================ RFF-bound */
const req = (v) =>
  v === undefined || v === null || v === "" ? "Required" : undefined;

// Inline validation message. Shown as soon as the field is required-and-empty
// (not only after a blur) because these gates block Calculate & Price — the user
// needs to see WHY the button is disabled without hunting for the field.
export const FieldError = ({ meta }) =>
  meta?.error && (meta.touched || meta.dirty || meta.submitFailed) ? (
    <Txt sx={{ fontSize: 11.5, color: SZ.bad, mt: "5px" }}>{meta.error}</Txt>
  ) : null;

/* ============================================================ Was markers */
/**
 * "was Purchase" under a field the user changed since the version on screen —
 * the change shown ON the field, not only in a summary somewhere else. The
 * scenario workspace provides a Map of dotted path → previous value (the same
 * `diffScenarioValues` rows its change count comes from); every Rff* field
 * here looks its own name up in it. Outside a provider nothing renders.
 */
const ChangedFieldsContext = createContext(null);
export const ChangedFieldsProvider = ChangedFieldsContext.Provider;

// RFF names arrays with brackets (`borrowers[0].ficoMid`); the diff uses dots.
const dotted = (name) => String(name || "").replace(/\[(\d+)\]/g, ".$1");

const WasMarker = ({ name }) => {
  const changed = useContext(ChangedFieldsContext);
  const from = changed?.get(dotted(name));
  if (from === undefined) return null;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        mt: "6px",
        fontSize: 11.5,
        lineHeight: "18px",
        px: "7px",
        borderRadius: "5px",
        color: "#94590A",
        background: "#FFF4E1",
        whiteSpace: "nowrap",
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      was{" "}
      <Box component="span" sx={{ fontFamily: SZ.mono, fontSize: 11 }}>
        {from === "—" ? "empty" : from}
      </Box>
    </Box>
  );
};

const WithWas = ({ name, children }) => (
  <>
    {children}
    <WasMarker name={name} />
  </>
);

export const RffChips = ({ name, options, sm, required }) => (
  <WithWas name={name}>
  <Field name={name} validate={required ? req : undefined}>
    {({ input }) => (
      <ChipGroup
        value={input.value}
        onChange={input.onChange}
        options={options}
        sm={sm}
      />
    )}
  </Field>
  </WithWas>
);

/**
 * Loan purpose — the backend's THREE values verbatim (Purchase / Refinance - No
 * Cash Out / Refinance - Cash Out). "Delayed Purchase" stopped being a purpose
 * on 2026-10-19 (REQ-106): it is the boolean `delayedPurchase`, asked only on a
 * cash-out refinance and priced/documented as a purchase. See
 * utils/loans/delayedPurchase.js for the parity helpers every purchase rule
 * reads.
 *
 * A legacy record still carrying the retired "Delayed Purchase" value is
 * seeded as cash-out + Yes (`normalizeLegacyDelayedPurchase`) before it reaches
 * the form; the chip mapping below is only a safety net for one that wasn't.
 */
/** Drop form-only keys (leading underscore) from a payload before posting. */
export const stripFormOnlyKeys = (payload) => {
  Object.keys(payload || {}).forEach((k) => {
    if (k.startsWith("_")) delete payload[k];
  });
  return payload;
};

export const RffPurposeChips = ({ name = "loanPurpose", options, sm, required }) => (
  <WithWas name={name}>
  <Field name={name} validate={required ? req : undefined}>
    {({ input }) => (
      <Field name={DELAYED_PURCHASE_FIELD} subscription={{ value: true }}>
        {({ input: flag }) => (
          <ChipGroup
            value={input.value === LEGACY_DELAYED_PURCHASE ? CASH_OUT_REFI : input.value}
            onChange={(v) => {
              // The flag only means something on a cash-out refinance (the
              // backend clears it otherwise) — leaving cash-out clears it, so
              // coming back asks it fresh.
              if (!canBeDelayedPurchase(v) && flag.value !== undefined) flag.onChange(undefined);
              input.onChange(v);
            }}
            options={(options || []).filter(
              (o) => (o?.value ?? o) !== LEGACY_DELAYED_PURCHASE,
            )}
            sm={sm}
          />
        )}
      </Field>
    )}
  </Field>
  </WithWas>
);

/**
 * "Delayed purchase?" — Yes / No chips (the same control as Term) bound to the
 * boolean `delayedPurchase`. Render it only while the purpose is a cash-out
 * refinance (`canBeDelayedPurchase`): it is REQUIRED while mounted, and an
 * unmounted field doesn't validate, so a hidden question never blocks a price.
 */
const YES_NO_BOOL = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];
export const RffDelayedPurchase = ({ purposeName = "loanPurpose", sm }) => (
  <WithWas name={DELAYED_PURCHASE_FIELD}>
  <Field
    name={DELAYED_PURCHASE_FIELD}
    validate={(v, all) =>
      canBeDelayedPurchase(all?.[purposeName]) && v !== true && v !== false
        ? "Required"
        : undefined
    }
  >
    {({ input, meta }) => (
      <>
        <ChipGroup value={input.value} onChange={input.onChange} options={YES_NO_BOOL} sm={sm} />
        <FieldError meta={meta} />
      </>
    )}
  </Field>
  </WithWas>
);

/**
 * "Delayed purchase?" for the plain-state sizers (DSCR Full + Quick Entry): the
 * same Yes / No chips as RffDelayedPurchase, bound to a boolean value.
 */
export const DelayedPurchaseChips = ({ value, onChange, sm = true }) => (
  <ChipGroup value={value} onChange={onChange} options={YES_NO_BOOL} sm={sm} />
);

/**
 * RFF-bound segmented switch for a boolean field. `type="checkbox"` is NOT used:
 * these are tri-state in practice (true / false / not answered), and RFF's
 * checkbox binding would collapse "unanswered" into `false` — which for
 * `dutchInterest` or `floodZone` is a claim, not an absence.
 *
 * `fallback` defaults to `false` so an untouched switch READS as "No" instead of
 * an empty track. It does not touch the form value — nothing is written until
 * the user picks, so the payload still omits the field entirely.
 */
export const RffSegmented = ({
  name,
  options,
  fullWidth,
  fallback = false,
}) => (
  <WithWas name={name}>
  <Field name={name}>
    {({ input }) => (
      <SegmentedGroup
        value={input.value === "" ? undefined : input.value}
        onChange={input.onChange}
        options={options}
        fullWidth={fullWidth}
        fallback={fallback}
      />
    )}
  </Field>
  </WithWas>
);

/**
 * Yes/No as a SWITCH.
 *
 * A boolean is not a choice between two things, it is one thing turned on or
 * off — and a two-segment pill spends the width of a real selector saying so.
 * The switch states its answer in one word beside it, which also means a card
 * full of them reads down the left edge as a column of on/off rather than as
 * six identical pairs of chips.
 *
 * Uncontrolled-safe: an unset field renders OFF (a boolean field with no value
 * is a "no" nobody has said yet), and the first click writes a real boolean.
 */
export const SwitchInput = ({
  value,
  onChange,
  labels = ["Yes", "No"],
  disabled,
}) => {
  const on = value === true;
  return (
    <Flx ac g={1.25} sx={{ height: 42 }}>
      <Box
        component="button"
        type="button"
        role="switch"
        aria-checked={on}
        disabled={disabled}
        onClick={() => onChange?.(!on)}
        sx={{
          width: 46,
          height: 27,
          flexShrink: 0,
          borderRadius: "999px",
          border: 0,
          // Padding, track height and knob are one set of numbers: the knob is
          // height minus twice the padding, so it sits flush top and bottom and
          // the travel is whatever width is left over.
          padding: "3px",
          display: "flex",
          justifyContent: on ? "flex-end" : "flex-start",
          alignItems: "center",
          background: on ? SZ.teal600 : "#c3ccd4",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
          transition: "background .15s",
          "&:hover": disabled
            ? {}
            : { background: on ? SZ.teal700 : "#adb9c3" },
          "&:focus-visible": {
            outline: `2px solid ${SZ.teal500}`,
            outlineOffset: 2,
          },
        }}
      >
        <Box
          sx={{
            width: 21,
            height: 21,
            borderRadius: "50%",
            background: "#fff",
            boxShadow: "0 1px 2px rgba(16,28,34,.28)",
          }}
        />
      </Box>
      <Txt
        sx={{
          fontSize: 13.5,
          fontWeight: 600,
          color: on ? SZ.teal700 : SZ.ink500,
        }}
      >
        {on ? labels[0] : labels[1]}
      </Txt>
    </Flx>
  );
};

export const RffSwitch = ({ name, labels }) => (
  <WithWas name={name}>
  <Field name={name}>
    {({ input }) => (
      <SwitchInput
        value={input.value === true}
        onChange={input.onChange}
        labels={labels}
      />
    )}
  </Field>
  </WithWas>
);

export const RffText = ({ name, placeholder, disabled, mono }) => (
  <WithWas name={name}>
  <Field name={name}>
    {({ input }) => (
      <TextInput
        {...input}
        placeholder={placeholder}
        disabled={disabled}
        mono={mono}
      />
    )}
  </Field>
  </WithWas>
);

export const RffMoney = ({ name, placeholder, required, inputSx }) => (
  <WithWas name={name}>
  <Field
    name={name}
    parse={(v) => (v === "" ? undefined : v)}
    validate={required ? req : undefined}
  >
    {({ input, meta }) => (
      <>
        <MoneyInput
          value={input.value}
          onChange={input.onChange}
          onBlur={input.onBlur}
          placeholder={placeholder}
          sx={inputSx}
        />
        <FieldError meta={meta} />
      </>
    )}
  </Field>
  </WithWas>
);

/**
 * Date. A native `<input type="date">` rather than the portal's MUI picker: the
 * sizers use no MUI inputs at all, and the one thing a picker would add here —
 * a calendar popper — does not justify dragging that control's styling in
 * behind it. The value is the browser's `YYYY-MM-DD`, which is exactly what the
 * model's datetime fields parse.
 */
export const RffDate = ({ name, required }) => (
  <WithWas name={name}>
  <Field
    name={name}
    parse={(v) => (v === "" ? undefined : v)}
    validate={required ? req : undefined}
  >
    {({ input, meta }) => (
      <>
        <Box
          component="input"
          type="date"
          value={input.value || ""}
          onChange={input.onChange}
          onBlur={input.onBlur}
          sx={{ ...baseInputSx, ...monoSx }}
        />
        <FieldError meta={meta} />
      </>
    )}
  </Field>
  </WithWas>
);

/**
 * `max` mirrors a hard `le=` bound on the model field, so an out-of-range value
 * is caught at the input instead of 422-ing on save. It compares the STORED
 * value, not the displayed one.
 */
export const RffPercent = ({
  name,
  factor = 1,
  decimals,
  placeholder,
  required,
  max,
  inputSx,
}) => (
  <WithWas name={name}>
  <Field
    name={name}
    parse={(v) => (v === "" ? undefined : v)}
    validate={(v) => {
      if (required) {
        const missing = req(v);
        if (missing) return missing;
      }
      if (max != null && v !== "" && v != null && Number(v) > max) {
        return `Maximum ${max}`;
      }
      return undefined;
    }}
  >
    {({ input, meta }) => (
      <>
        <PercentInput
          value={input.value}
          onChange={input.onChange}
          onBlur={input.onBlur}
          factor={factor}
          decimals={decimals}
          placeholder={placeholder}
          sx={inputSx}
        />
        <FieldError meta={meta} />
      </>
    )}
  </Field>
  </WithWas>
);

export const RffNum = ({ name, placeholder, required }) => (
  <WithWas name={name}>
  <Field
    name={name}
    parse={(v) => (v === "" ? undefined : v)}
    validate={required ? req : undefined}
  >
    {({ input, meta }) => (
      <>
        <NumInput
          value={input.value}
          onChange={input.onChange}
          onBlur={input.onBlur}
          placeholder={placeholder}
        />
        <FieldError meta={meta} />
      </>
    )}
  </Field>
  </WithWas>
);

export const RffStepper = ({ name, placeholder, required, min = 0, max }) => (
  <WithWas name={name}>
  <Field
    name={name}
    parse={(v) => (v === "" ? undefined : v)}
    validate={required ? req : undefined}
  >
    {({ input, meta }) => (
      <>
        <StepperInput
          value={input.value}
          onChange={input.onChange}
          onBlur={input.onBlur}
          placeholder={placeholder}
          min={min}
          max={max}
        />
        <FieldError meta={meta} />
      </>
    )}
  </Field>
  </WithWas>
);

/**
 * `clearable` is for an option list that carries its own `{value: null}` row —
 * a field the user must be able to go BACK to unanswered on, which the disabled
 * placeholder row cannot do. RFF holds an untouched field as `""`, so without
 * this the null row would never match and the control would render blank on
 * mount instead of showing that row's label.
 */
export const RffSelect = ({
  name,
  options,
  required,
  placeholder,
  clearable,
}) => (
  <WithWas name={name}>
  <Field name={name} validate={required ? req : undefined}>
    {({ input }) => (
      <SelectInput
        value={
          clearable && (input.value === "" || input.value === undefined)
            ? null
            : input.value
        }
        onChange={input.onChange}
        options={options}
        placeholder={clearable ? undefined : placeholder}
      />
    )}
  </Field>
  </WithWas>
);

/**
 * RFF-bound TRI-STATE boolean select — true / false / not answered.
 *
 * The distinction is the whole point, and it is why this is a select and not a
 * switch or a segmented pair. Several backend booleans read the three states
 * differently: `approvedPermitsAndPlans` fires its −10% front-end reduction on
 * an EXPLICIT `false` and does nothing at all when unset, and `financedReserve`
 * is REQUIRED once reserve months are entered — "not stated" is a real answer
 * the engine reports back as a `dataChecks` row. A two-state control cannot say
 * "nobody has answered this", so it would post a claim the user never made.
 *
 * The "not stated" option is REAL and selectable, carrying `null` — not the
 * disabled placeholder row `SelectInput` draws, which cannot be chosen. Without
 * it a field is one-way: pick Yes once and there is no way back to unanswered,
 * which for a tri-state control is the state most worth being able to return to.
 * `null` posts as JSON null and arrives as Python `None`, which is exactly what
 * "nobody has answered this" means on the model.
 *
 * Built on `SelectInput` rather than `BoolSelectInput`: that one round-trips
 * through strings to survive a native `<select>`, and this menu passes the raw
 * option value straight through — so real booleans stay real booleans and the
 * third state stays distinguishable from `false`.
 */
export const RffBoolSelect = ({
  name,
  options,
  placeholder = "Not stated",
  required,
}) => (
  <WithWas name={name}>
  <Field name={name} validate={required ? req : undefined}>
    {({ input, meta }) => (
      <>
        <SelectInput
          value={
            input.value === "" || input.value === undefined ? null : input.value
          }
          onChange={input.onChange}
          options={[{ value: null, label: placeholder }, ...options]}
        />
        <FieldError meta={meta} />
      </>
    )}
  </Field>
  </WithWas>
);

/* ============================================================ Breakdown bar */
export const BreakdownBar = ({
  label = "Loan breakdown",
  total,
  segments = [],
  legend = [],
  note,
}) => {
  const sum = segments.reduce((s, x) => s + (x.value || 0), 0) || 1;
  return (
    <Box
      sx={{
        border: `1px solid ${SZ.line}`,
        borderRadius: SZ.radiusSm,
        background: "#fbfcfc",
        p: "14px 16px",
        mt: 2,
      }}
    >
      <Flx jb sx={{ alignItems: "baseline", mb: 1.25 }}>
        <Txt
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: SZ.ink500,
          }}
        >
          {label}
        </Txt>
        {total != null && (
          <Txt sx={{ fontSize: 13, color: SZ.ink500 }}>
            Total loan request{" "}
            <Box
              component="strong"
              sx={{
                fontFamily: SZ.mono,
                fontSize: 15,
                color: SZ.ink900,
                fontWeight: 600,
              }}
            >
              {total}
            </Box>
          </Txt>
        )}
      </Flx>
      <Box
        sx={{
          display: "flex",
          height: 26,
          borderRadius: "6px",
          overflow: "hidden",
        }}
      >
        {segments.map((s, i) => {
          const pct = (s.value / sum) * 100;
          return (
            <Box
              key={i}
              sx={{
                width: `${pct}%`,
                minWidth: 0,
                display: "grid",
                placeItems: "center",
                color: "#fff",
                fontSize: 11.5,
                fontWeight: 600,
                background: s.color || SZ.teal600,
                transition: "width .3s ease",
                overflow: "hidden",
              }}
            >
              {pct > 18 ? s.label : ""}
            </Box>
          );
        })}
      </Box>
      {legend.length > 0 && (
        <Flx sx={{ gap: 2.25, mt: 1.1, flexWrap: "wrap" }}>
          {legend.map((l, i) => (
            <Flx key={i} ac g={0.75}>
              <Box
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: "3px",
                  background: l.color,
                }}
              />
              <Txt sx={{ fontSize: 12, color: SZ.ink500 }}>
                {l.label}{" "}
                <Box
                  component="strong"
                  sx={{
                    fontFamily: SZ.mono,
                    color: SZ.ink900,
                    fontWeight: 500,
                  }}
                >
                  {l.value}
                </Box>
              </Txt>
            </Flx>
          ))}
        </Flx>
      )}
      {note && (
        <Box
          sx={{
            mt: 1.25,
            p: "8px 11px",
            borderRadius: "6px",
            background: SZ.warnBg,
            color: SZ.warn,
            fontSize: 12,
          }}
        >
          {note}
        </Box>
      )}
    </Box>
  );
};

/* ============================================================ Repeater */
/**
 * The label above a group of fields inside a card.
 *
 * Small, spaced and upper-case: it is a signpost between groups, not a heading
 * competing with the card's own title. Every group after the first also opens
 * with a hairline, so where one subject ends and the next begins is visible
 * without reading a word of it.
 */
export const SubLabel = ({ children, note, first }) => (
  <Box
    sx={{
      borderTop: first ? "none" : `1px solid ${SZ.line}`,
      mt: first ? 0 : "28px",
      pt: first ? 0 : "28px",
    }}
  >
    <Txt
      component="div"
      sx={{
        height: 22,
        display: "flex",
        alignItems: "center",
        gap: "8px",
        fontSize: 11.5,
        fontWeight: 600,
        letterSpacing: ".08em",
        textTransform: "uppercase",
        color: SZ.ink400,
        mb: "14px",
      }}
    >
      {children}
      {/* The qualifier — "last 3 years", "optional until loan creation". Left in
          sentence case so it reads as an aside rather than as more heading. */}
      {note && (
        <Box
          component="span"
          sx={{
            fontSize: 11.5,
            fontWeight: 500,
            letterSpacing: ".02em",
            textTransform: "none",
            opacity: 0.85,
          }}
        >
          {note}
        </Box>
      )}
    </Txt>
  </Box>
);

const IconBtn = ({ danger, onClick, label, children }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    aria-label={label}
    sx={{
      border: 0,
      background: "transparent",
      cursor: "pointer",
      width: 28,
      height: 28,
      borderRadius: "6px",
      display: "grid",
      placeItems: "center",
      color: SZ.ink400,
      transition: "all .12s",
      "&:hover": danger
        ? { background: SZ.badBg, color: SZ.bad }
        : { background: "#e9f0f1", color: SZ.ink700 },
    }}
  >
    {children}
  </Box>
);

const Chevron = ({ open }) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transform: open ? "none" : "rotate(-90deg)",
      transition: "transform .18s",
    }}
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
  >
    <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14M10 11v6M14 11v6" />
  </svg>
);

// Collapsible repeater item with header (title + sub) and remove control.
export const RepeatItem = ({
  title,
  sub,
  onRemove,
  removeLabel,
  defaultCollapsed = false,
  children,
}) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  return (
    <Box
      sx={{
        border: `1px solid ${SZ.line}`,
        borderRadius: SZ.radiusSm,
        mb: 1.5,
        overflow: "hidden",
        transition: "border-color .15s",
        "&:hover": { borderColor: SZ.lineStrong },
      }}
    >
      <Flx
        jb
        ac
        onClick={() => setCollapsed((v) => !v)}
        sx={{
          p: "11px 14px",
          background: "#fafcfc",
          borderBottom: collapsed ? "none" : `1px solid ${SZ.line}`,
          cursor: "pointer",
          userSelect: "none",
          "&:hover": { background: "#f3f8f9" },
        }}
      >
        <Box>
          <Txt
            component="span"
            sx={{ fontSize: 13, fontWeight: 600, color: SZ.ink900 }}
          >
            {title}
          </Txt>
          {sub && (
            <Txt
              component="span"
              sx={{ fontSize: 12, color: SZ.ink400, ml: 1 }}
            >
              {sub}
            </Txt>
          )}
        </Box>
        <Flx ac g={0.5} onClick={(e) => e.stopPropagation()}>
          {onRemove && (
            <IconBtn danger onClick={onRemove} label={removeLabel || "Remove"}>
              <TrashIcon />
            </IconBtn>
          )}
          <IconBtn onClick={() => setCollapsed((v) => !v)} label="Collapse">
            <Chevron open={!collapsed} />
          </IconBtn>
        </Flx>
      </Flx>
      {/* 28px top and bottom, the same measure `SubLabel` puts between groups —
          so the FIRST group's label and the LAST group's fields sit the same
          distance from the card edge as every divider inside it. */}
      {!collapsed && <Box sx={{ p: "28px 16px" }}>{children}</Box>}
    </Box>
  );
};

const PlusIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export const AddButton = ({ onClick, children }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      // Full width and teal-edged: it is the only control on its row, and what
      // it adds is a whole card — so it reads as the slot the next one goes
      // into rather than as a small secondary button parked on the left.
      display: "flex",
      width: "100%",
      alignItems: "center",
      justifyContent: "center",
      gap: 0.9,
      border: `1px dashed ${SZ.teal500}`,
      background: "transparent",
      color: SZ.teal700,
      fontSize: 13,
      fontWeight: 400,
      fontFamily: "inherit",
      padding: "11px 16px",
      borderRadius: SZ.radiusSm,
      cursor: "pointer",
      transition: "all .12s",
      "&:hover": { background: SZ.teal50, borderColor: SZ.teal600 },
    }}
  >
    <PlusIcon />
    {children}
  </Box>
);

/* ============================================================ Actions */
/**
 * The form's action row.
 *
 * `sticky` pins it to the bottom of the viewport while the card is on screen —
 * for a form long enough that Calculate & price would otherwise be several
 * screens below the fields being edited. It bleeds to the card's edges (the
 * negative margins undo the card's padding, the padding puts it back inside) so
 * it reads as the card's footer rather than a row floating over the content.
 *
 * Opt-in, because it only pays off on a tall form: the DSCR sizers' action rows
 * sit a screen from the top of their cards and are better left in the flow.
 */
/**
 * The action bar, pinned to the bottom of the VIEWPORT.
 *
 * Full bleed — the surface spans the whole window, under the app's nav rail
 * included, so the primary action is always the last thing on screen no matter
 * how far down a long scenario the user has scrolled. Its CONTENT is a centred
 * `maxWidth` column, the same treatment the page itself gets, so the buttons
 * do not wander to the window edge on a wide monitor.
 *
 * `alignTo` is a ref to the page's own content root: the bar starts at that
 * element's left edge instead of the window's, so it clears the admin nav rail
 * — and clears it by MEASURING rather than by knowing that the rail is 224px
 * open and 60px collapsed, which is state this file cannot see and which would
 * be wrong the day either number changes.
 *
 * Render it INSIDE the `<form>`: fixed positioning is independent of the DOM
 * parent, but `type="submit"` is not.
 */
export const FixedActionBar = ({
  alignTo,
  maxWidth = SIZER_MAX_W,
  children,
}) => {
  // `null` until measured. Rendering at left:0 first would flash a full-width
  // bar across the nav sidebar — the exact overlap this component measures to
  // avoid — so it holds off for the one layout pass instead.
  const [box, setBox] = useState(null);

  /* A PASSIVE effect, and that is the whole fix.
   *
   * `alignTo` points at `SizerShell`'s root — an ANCESTOR of this bar. React
   * attaches host refs during the layout phase bottom-up, so a descendant's
   * `useLayoutEffect` runs BEFORE its ancestor's ref is attached:
   * `alignTo.current` is still null here on mount. The measurement therefore
   * fell through to the no-anchor branch and pinned the bar to left:0 — spanning
   * the full viewport under the sidebar — and the deps are `[alignTo]`, a stable
   * ref object, so it never re-ran to correct itself. What that looked like was
   * a bar whose CONTENT sat half the sidebar's width too far left, because the
   * centred inner box was centring inside a container that started at 0 instead
   * of at the form column.
   *
   * Passive effects run after the commit completes for the whole tree, by which
   * point every ref is attached. The bar renders one frame late; the null guard
   * below means that frame is empty rather than misplaced.
   */
  useEffect(() => {
    const el = alignTo?.current;
    // No anchor to align to: sit against the viewport rather than never render.
    if (!el) {
      setBox({ left: 0, right: 0 });
      return undefined;
    }
    const measure = () => {
      const r = el.getBoundingClientRect();
      /* BOTH edges, not just the left. The admin main column owns the section's
         vertical scroll, so its right edge stops a scrollbar's width short of
         the viewport while a `right: 0` bar runs the whole way — and a centred
         inner box inside a container ~15px wider lands ~7px off from the cards
         above it. Mirroring the anchor's own rect makes the two boxes
         identical, scrollbar included. */
      const next = {
        left: r.left,
        right: Math.max(0, window.innerWidth - r.right),
      };
      // Only on CHANGE — this observes an element that contains the bar.
      setBox((prev) =>
        prev &&
        Math.abs(prev.left - next.left) < 0.5 &&
        Math.abs(prev.right - next.right) < 0.5
          ? prev
          : next,
      );
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    ro.observe(document.body);
    window.addEventListener("resize", measure);
    /* The admin sidebar COLLAPSES to an icon rail over a 220ms width
       transition, which slides this bar's left edge with it. ResizeObserver
       reports the start and end of that but is not guaranteed per-frame, and it
       never fires at all if the main column's box happens to settle back to the
       same size — so the bar could finish a collapse still aligned to where the
       sidebar used to be. A capture-phase transitionend catches the settle. */
    document.addEventListener("transitionend", measure, true);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
      document.removeEventListener("transitionend", measure, true);
    };
  }, [alignTo]);

  if (box == null) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        left: box.left,
        right: box.right,
        bottom: 0,
        /* UNDER the app chrome, deliberately. The nav sidebar sits at 111-222
           and the admin narrow-window drawer at 95-96; at the old 1100 this bar
           floated in front of both, so opening the drawer left a strip of form
           actions lying across it. Above page content, below anything that is
           meant to cover the page. */
        zIndex: 90,
        /* OPAQUE. `backdrop-filter` was tried here twice — on the element and on
           a ::before layer — and neither composited: this bar is `position:
           fixed` inside SizersLayout's `overflow-y: auto` main column, which is a
           documented way to lose the backdrop. What it left behind was a bar
           with only alpha and no blur, so the cards scrolling under it read
           straight through. Solid is the honest version of that until there is a
           way to verify the frosted look in a browser. */
        // background: SZ.card,
        // background: "#ffffffd1",
        background: "#ffffffd1",
        backdropFilter: "blur(2px)",
        borderTop: `1px solid ${SZ.line}`,
        boxShadow: "0 -8px 20px -12px rgba(16,28,34,.45)",
      }}
    >
      {/* Same max-width AND same padding as SizerShell's inner container, from
          the same token — the bar's buttons line up with the column of cards
          above them only if both boxes are measured identically. They drifted
          when this defaulted to 1500 against the shell's 1400: on a wide screen
          the bar's content sat 50px outside the form on each side. */}
      <Box sx={{ maxWidth, mx: "auto", px: SIZER_GUTTER, py: 1.75 }}>
        {children}
      </Box>
    </Box>
  );
};

export const ActionBar = ({ children, sticky }) => (
  <Flx
    ac
    g={1.75}
    sx={{
      pt: 2.75,
      mt: 3,
      borderTop: `1px solid ${SZ.line}`,
      flexWrap: "wrap",
      ...(sticky
        ? {
            position: "sticky",
            bottom: 0,
            zIndex: 2,
            background: SZ.card,
            mx: { xs: -2.5, md: "-26px" },
            mb: { xs: -2.5, md: "-26px" },
            px: { xs: 2.5, md: "26px" },
            pb: { xs: 2.5, md: "26px" },
            // Reads as a hairline at rest and as a lifted edge once content
            // scrolls under it.
            boxShadow: "0 -6px 14px -10px rgba(16,28,34,.35)",
          }
        : null),
    }}
  >
    {children}
  </Flx>
);

/**
 * `subLabel` puts the scenario's one-line description INSIDE the button, under
 * its label in smaller type — "Calculate & price / 1 applicant · 1 property".
 *
 * It reads better there than beside the button: the line describes what is about
 * to be priced, so it belongs on the control that prices it, and the action bar
 * stops being two competing columns of text. The button grows to two lines and
 * the sub-line truncates rather than wrapping, so the bar's height is stable no
 * matter how long the scenario description gets.
 */
export const PrimaryButton = ({
  onClick,
  disabled,
  type = "button",
  startIcon,
  subLabel,
  children,
  sx,
}) => (
  <Box
    component="button"
    type={type}
    onClick={onClick}
    disabled={disabled}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 1,
      background: SZ.teal600,
      color: "#fff",
      border: 0,
      borderRadius: SZ.radiusSm,
      padding: subLabel ? "6px 22px" : "0 22px",
      height: subLabel ? "auto" : 42,
      minHeight: 42,
      fontSize: 14,
      fontWeight: 600,
      fontFamily: "inherit",
      cursor: disabled ? "default" : "pointer",
      opacity: disabled ? 0.6 : 1,
      boxShadow: "0 1px 2px rgba(15,23,42,.18)",
      transition: "background .12s, box-shadow .12s",
      "&:hover": disabled ? {} : { background: SZ.teal700 },
      "& svg": { fontSize: 18 },
      ...sx,
    }}
  >
    {startIcon}
    {subLabel ? (
      <Box sx={{ minWidth: 0, textAlign: "center", lineHeight: 1.2 }}>
        <Box component="span" sx={{ display: "block" }}>
          {children}
        </Box>
        <Box
          component="span"
          sx={{
            display: "block",
            mt: "1px",
            fontSize: 10.5,
            fontWeight: 500,
            opacity: 0.85,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {subLabel}
        </Box>
      </Box>
    ) : (
      children
    )}
  </Box>
);

export const SecondaryButton = ({
  onClick,
  disabled,
  startIcon,
  children,
  sx,
}) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    disabled={disabled}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.9,
      background: SZ.card,
      color: SZ.ink700,
      border: `1px solid ${SZ.lineStrong}`,
      borderRadius: SZ.radiusSm,
      padding: "0 18px",
      height: 42,
      fontSize: 13.5,
      fontWeight: 600,
      fontFamily: "inherit",
      cursor: disabled ? "default" : "pointer",
      opacity: disabled ? 0.6 : 1,
      transition: "all .12s",
      "&:hover": disabled
        ? {}
        : { borderColor: SZ.teal500, color: SZ.teal700, background: SZ.teal50 },
      "& svg": { fontSize: 17 },
      ...sx,
    }}
  >
    {startIcon}
    {children}
  </Box>
);

/**
 * Who may see the sizer dev tools.
 *
 * They used to be a development-build-only affordance. They now also ship to
 * PRODUCTION for super admins — the desk asked for the demo fill and the state
 * spy on the live app — which is why they are labelled: an internal tool that
 * reaches production must say out loud that not everyone can see it, or the
 * first screenshot in a support thread reads as a missing feature for everyone
 * else.
 *
 * Super admin is `usePermissionsHook().isSuperAdmin` — the same explicit email
 * allowlist that gates the Users tab and the Lock Queue. Not `isAdmin`: that is
 * a backend `admin` permission held by ordinary lender staff, and a button that
 * overwrites a form with a fabricated scenario is not something to hand them.
 */
export const useSizerDevToolsVisible = () => {
  const { isSuperAdmin } = usePermissionsHook();
  return SIZER_DEV_BUILD || Boolean(isSuperAdmin);
};

/**
 * The "Admin only" marker on the dev-tool group. Deliberately quiet — it is a
 * caption on the controls beside it, not a control itself.
 */
export const AdminOnlyBadge = ({ children = "Admin only" }) => (
  <Box
    component="span"
    sx={{
      fontSize: 9.5,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: SZ.ink400,
      background: "#eef2f3",
      border: `1px solid ${SZ.line}`,
      borderRadius: "99px",
      padding: "3px 9px",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </Box>
);

/**
 * DemoFillButton — the "Fill demo scenario" header action. Renders nothing for a
 * user who may not see the dev tools, so a screen can drop it into
 * `SizerShell`'s `headerAction` unconditionally.
 */
export const DemoFillButton = ({
  onClick,
  children = "Fill demo scenario",
}) => {
  const visible = useSizerDevToolsVisible();
  if (!visible) return null;
  return (
    <SecondaryButton onClick={onClick} startIcon={<AutoFixHighIcon />}>
      {children}
    </SecondaryButton>
  );
};

/**
 * The sizers' dev-tool state: whether the live-values spy is showing.
 *
 * The spy is a debugging aid, so it starts CLOSED — it used to mount expanded
 * under every form, pushing the whole page down before anyone asked for it.
 * `enabled` carries the same visibility test the buttons use, so the panel can
 * never be left open for a user who lost access to the toggle.
 */
export const useSizerDevTools = () => {
  const [spyOpen, setSpyOpen] = useState(false);
  const [payloadOpen, setPayloadOpen] = useState(false);
  const enabled = useSizerDevToolsVisible();
  return {
    enabled,
    spyOpen: enabled && spyOpen,
    toggleSpy: () => setSpyOpen((v) => !v),
    payloadOpen: enabled && payloadOpen,
    togglePayload: () => setPayloadOpen((v) => !v),
  };
};

/** Toggle for the form spy. Sits beside the demo-fill button. */
export const SpyToggleButton = ({ open, onClick }) => {
  const visible = useSizerDevToolsVisible();
  if (!visible) return null;
  return (
    <SecondaryButton
      onClick={onClick}
      startIcon={<CodeRoundedIcon />}
      sx={
        open
          ? {
              borderColor: SZ.teal500,
              color: SZ.teal700,
              background: SZ.teal50,
            }
          : undefined
      }
    >
      {open ? "Hide form spy" : "Form spy"}
    </SecondaryButton>
  );
};

/**
 * SizerDevBar — the dev-tool group, in its standard order, under one "Admin
 * only" badge. One badge for the group rather than one per button: they always
 * render together on the same row, and two identical labels 8px apart would read
 * as decoration rather than a restriction.
 *
 * It sits UNDER THE FORM at the bottom of the left column, not in the page
 * header.
 *
 * The header is where a user looks for what the page is; these controls are
 * scaffolding for whoever is building the form, and one of them overwrites every
 * field. Putting them last, below the thing they act on and visually separated
 * from it, keeps the top of the page about the scenario. The dashed rule and
 * recessed fill say "tooling" without needing a heading.
 *
 * Renders nothing for a user who fails `useSizerDevToolsVisible`, so a screen
 * can mount it unconditionally.
 */
/**
 * "Admin tools" — the build tools (Payload, Form spy, Fill demo scenario) as a
 * header button with a popover menu. Same visibility gate as the bar
 * (super admins, or a development build). The two toggles show a check while
 * their panel is open; the panels themselves still render under the form.
 */
const SizerDevMenu = ({
  onFillDemo,
  fillLabel = "Fill demo scenario",
  spyOpen,
  onToggleSpy,
  payloadOpen,
  onTogglePayload,
}) => {
  const [anchor, setAnchor] = useState(null);
  const close = () => setAnchor(null);
  const item = (key, icon, label, onClick, checked) => (
    <MenuItem
      key={key}
      onClick={() => {
        close();
        onClick();
      }}
      sx={{ fontSize: 13.5, gap: 1.25, minWidth: 220 }}
    >
      <Box component="span" sx={{ display: "inline-flex", color: SZ.ink500, "& svg": { fontSize: 18 } }}>
        {icon}
      </Box>
      <Box component="span" sx={{ flex: 1 }}>
        {label}
      </Box>
      {checked && <CheckRoundedIcon sx={{ fontSize: 17, color: SZ.teal600 }} />}
    </MenuItem>
  );
  return (
    <>
      <SecondaryButton
        onClick={(e) => setAnchor(e.currentTarget)}
        startIcon={<CodeRoundedIcon />}
        sx={{ height: 36, minHeight: 36, padding: "0 14px", fontSize: 13, "& svg": { fontSize: 16 } }}
      >
        Admin tools
      </SecondaryButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { mt: 0.75 } } }}
      >
        <Box sx={{ px: 2, pt: 0.75, pb: 1 }}>
          <AdminOnlyBadge />
          <Txt component="div" sx={{ fontSize: 11.5, color: SZ.ink400, mt: 0.75 }}>
            Build tools for this form — not part of the scenario.
          </Txt>
        </Box>
        {onTogglePayload &&
          item("payload", <DataObjectRounded />, "Payload", onTogglePayload, payloadOpen)}
        {onToggleSpy && item("spy", <CodeRoundedIcon />, "Form spy", onToggleSpy, spyOpen)}
        {onFillDemo && item("demo", <AutoFixHighIcon />, fillLabel, onFillDemo, false)}
      </Menu>
    </>
  );
};

export const SizerDevBar = ({
  onFillDemo,
  spyOpen,
  onToggleSpy,
  payloadOpen,
  onTogglePayload,
  fillLabel,
  note = "Build tools for this form — not part of the scenario.",
  sx,
  // "menu": an "Admin tools" button + popover menu for a page header (next
  // to the version picker), instead of the dashed bar under the form.
  variant = "bar",
}) => {
  const visible = useSizerDevToolsVisible();
  if (!visible) return null;
  if (variant === "menu") {
    return (
      <SizerDevMenu
        onFillDemo={onFillDemo}
        fillLabel={fillLabel}
        spyOpen={spyOpen}
        onToggleSpy={onToggleSpy}
        payloadOpen={payloadOpen}
        onTogglePayload={onTogglePayload}
      />
    );
  }
  return (
    <Box
      sx={{
        mt: 2,
        p: "12px 16px",
        border: `1px dashed ${SZ.lineStrong}`,
        borderRadius: SZ.radius,
        background: "#fbfcfc",
        ...sx,
      }}
    >
      <Flx jb ac g={1.5} sx={{ flexWrap: "wrap" }}>
        <Flx ac g={1} sx={{ flexWrap: "wrap" }}>
          <AdminOnlyBadge />
          {note && <Txt sx={{ fontSize: 11.5, color: SZ.ink400 }}>{note}</Txt>}
        </Flx>
        <Flx ac g={1} sx={{ flexWrap: "wrap" }}>
          {onTogglePayload && (
            <PayloadToggleButton open={payloadOpen} onClick={onTogglePayload} />
          )}
          {onToggleSpy && (
            <SpyToggleButton open={spyOpen} onClick={onToggleSpy} />
          )}
          {onFillDemo && (
            <DemoFillButton onClick={onFillDemo}>{fillLabel}</DemoFillButton>
          )}
        </Flx>
      </Flx>
    </Box>
  );
};

/**
 * SizerStateSpy — the live-values panel for the sizers that are NOT
 * react-final-form (the DSCR sizer and DSCR Quick Entry hold plain useState).
 * RffFormSpy needs a form context, so this is its plain-object twin: same card,
 * same JSON view, same visibility gate.
 *
 * `enabled` comes from `useSizerDevTools().spyOpen` — the panel is off until the
 * header's Form spy button turns it on, and cannot appear at all for a user who
 * fails `useSizerDevToolsVisible` (development build, or a super admin).
 */
export const SizerStateSpy = ({ values, enabled }) => {
  if (!enabled) return null;
  return (
    <Box
      sx={{
        mt: 2,
        bgcolor: SZ.card,
        border: `1px solid ${SZ.line}`,
        borderRadius: SZ.radius,
        boxShadow: SZ.shadow,
        p: 2,
        ".json-view div": { margin: "3px 0" },
      }}
    >
      <Txt
        component="div"
        sx={{ fontSize: 12, fontWeight: 700, color: SZ.ink500, mb: 1.25 }}
      >
        Form Spy
      </Txt>
      <JsonView src={values} theme="atom" enableClipboard displaySize />
    </Box>
  );
};

/**
 * Toggle for the payload inspector. Same gate as the form spy — a super admin,
 * or a development build.
 */
export const PayloadToggleButton = ({ open, onClick }) => {
  const visible = useSizerDevToolsVisible();
  if (!visible) return null;
  return (
    <SecondaryButton
      onClick={onClick}
      startIcon={<DataObjectRounded />}
      sx={
        open
          ? {
              borderColor: SZ.teal500,
              color: SZ.teal700,
              background: SZ.teal50,
            }
          : undefined
      }
    >
      {open ? "Hide payload" : "Payload"}
    </SecondaryButton>
  );
};

/**
 * SizerPayloadPanel — the EXACT bodies this screen would POST, side by side with
 * the endpoints they go to.
 *
 * The form spy shows form STATE; this shows the wire. They differ by everything
 * `buildPayload` does — the entity folded into `entities[]`, the scenario-stage
 * stubs stripped on the save path — and that difference is precisely what is
 * worth being able to see before creating a live loan.
 *
 * `sections` is `[{label, endpoint, note, value}]`, rendered in order.
 */
export const SizerPayloadPanel = ({ enabled, sections = [] }) => {
  if (!enabled) return null;
  return (
    <Box
      sx={{
        mt: 2,
        bgcolor: SZ.card,
        border: `1px solid ${SZ.line}`,
        borderRadius: SZ.radius,
        boxShadow: SZ.shadow,
        p: 2,
        ".json-view div": { margin: "3px 0" },
      }}
    >
      <Flx ac g={1} sx={{ mb: 1.5 }}>
        <Txt sx={{ fontSize: 12, fontWeight: 700, color: SZ.ink500 }}>
          Outgoing payload
        </Txt>
        <AdminOnlyBadge />
      </Flx>
      {sections.map((s, i) => (
        <Box key={s.label} sx={{ mt: i ? 2.5 : 0 }}>
          <Flx ac g={1} sx={{ mb: 0.75, flexWrap: "wrap" }}>
            <Txt sx={{ fontSize: 12, fontWeight: 700, color: SZ.ink900 }}>
              {s.label}
            </Txt>
            {s.endpoint && (
              <Box
                component="span"
                sx={{
                  fontFamily: SZ.mono,
                  fontSize: 10.5,
                  color: SZ.ink500,
                  background: SZ.bg,
                  border: `1px solid ${SZ.line}`,
                  borderRadius: "5px",
                  padding: "2px 7px",
                }}
              >
                {s.endpoint}
              </Box>
            )}
          </Flx>
          {s.note && (
            <Txt
              component="div"
              sx={{
                fontSize: 11,
                color: SZ.ink400,
                mb: 0.75,
                fontStyle: "italic",
              }}
            >
              {s.note}
            </Txt>
          )}
          <JsonView
            src={s.value}
            theme="atom"
            enableClipboard
            displaySize
            collapsed={2}
          />
        </Box>
      ))}
    </Box>
  );
};

export const ActionNote = ({ children }) => (
  <Txt sx={{ fontSize: 12.5, color: SZ.ink400 }}>{children}</Txt>
);

/* ============================================================ Results card */
export const StatusPill = ({ state = "idle", children }) => {
  const map = {
    idle: { bg: "#eef1f2", fg: SZ.ink500 },
    est: { bg: SZ.warnBg, fg: SZ.warn },
    live: { bg: SZ.goodBg, fg: SZ.good },
  };
  const s = map[state] || map.idle;
  return (
    <Box
      sx={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.03em",
        // Own line-height, and never stretch: dropped into an AG Grid cell the
        // pill would otherwise inherit the cell's row-height line-height (and
        // be stretched by the flex row), rendering as a full-height block.
        lineHeight: 1.45,
        alignSelf: "center",
        padding: "3px 9px",
        borderRadius: "99px",
        background: s.bg,
        color: s.fg,
      }}
    >
      {children}
    </Box>
  );
};

export const ResultsCard = ({ title, pill, foot, children }) => (
  <Box
    sx={{
      bgcolor: SZ.card,
      border: `1px solid ${SZ.line}`,
      borderRadius: SZ.radius,
      boxShadow: SZ.shadow,
      overflow: "hidden",
    }}
  >
    <Flx
      jb
      ac
      g={1}
      // Wraps: a status pill can carry a sentence ("Requires exception request
      // to proceed"), and in the 360px rail that would otherwise crush the
      // title rather than take its own line.
      sx={{
        p: "16px 20px",
        borderBottom: `1px solid ${SZ.line}`,
        flexWrap: "wrap",
      }}
    >
      <Txt
        sx={{
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: "0.02em",
          color: SZ.ink900,
        }}
      >
        {title}
      </Txt>
      {pill}
    </Flx>
    <Box sx={{ p: "20px" }}>{children}</Box>
    {foot && (
      <Box
        sx={{
          p: "12px 20px",
          borderTop: `1px solid ${SZ.line}`,
          fontSize: 11.5,
          color: SZ.ink400,
          background: "#fbfcfc",
        }}
      >
        {foot}
      </Box>
    )}
  </Box>
);

/**
 * SurfaceCard / SurfaceCardHead — the card shell of a FULL-WIDTH results surface
 * (the one a screen switches to under `StackedWorkbench`, once the answer is too
 * big for the 360px rail).
 *
 * It is `ResultsCard`'s wide twin: no body padding, because a wide card's
 * contents own their own gutters (a table bleeds to the card edge, a summary
 * grid draws its own dividers), and a head that carries a subtitle, a context
 * line and a right-hand slot rather than just a title and a pill.
 *
 * Both DSCR (`DscrResultsView`) and RTL (`RtlResultsView`) render into these, so
 * the two priced surfaces stay one idiom.
 */
export const SurfaceCard = ({ children, sx }) => (
  <Box
    sx={{
      bgcolor: SZ.card,
      border: `1px solid ${SZ.line}`,
      borderRadius: SZ.radius,
      boxShadow: SZ.shadow,
      overflow: "hidden",
      ...sx,
    }}
  >
    {children}
  </Box>
);

export const SurfaceCardHead = ({ title, sub, ctx, right }) => (
  <Flx
    jb
    ac
    g={1.5}
    sx={{
      p: "14px 20px",
      borderBottom: `1px solid ${SZ.line}`,
      flexWrap: "wrap",
    }}
  >
    <Box>
      <Txt
        component="div"
        sx={{ fontSize: 13.5, fontWeight: 700, color: SZ.ink900 }}
      >
        {title}
      </Txt>
      {sub && (
        <Txt
          component="div"
          sx={{ fontSize: 11.5, color: SZ.ink400, mt: "2px" }}
        >
          {sub}
        </Txt>
      )}
    </Box>
    <Flx ac g={1.5} sx={{ flexWrap: "wrap" }}>
      {ctx && <Txt sx={{ fontSize: 11.5, color: SZ.ink400 }}>{ctx}</Txt>}
      {right}
    </Flx>
  </Flx>
);

export const EmptyState = ({ icon, title, description }) => (
  <Flx column center sx={{ textAlign: "center", p: "16px" }}>
    {icon && (
      <Flx
        center
        sx={{
          width: 44,
          height: 44,
          borderRadius: "12px",
          background: SZ.teal50,
          color: SZ.teal600,
          mb: 1.75,
          "& svg": { fontSize: 20 },
        }}
      >
        {icon}
      </Flx>
    )}
    <Txt sx={{ fontSize: 14, fontWeight: 600, color: SZ.ink900, mb: 0.6 }}>
      {title}
    </Txt>
    {description && (
      <Txt sx={{ fontSize: 12.5, color: SZ.ink500, maxWidth: 240 }}>
        {description}
      </Txt>
    )}
  </Flx>
);

export const HeroMetric = ({ label, value, sub }) => (
  <Box sx={{ textAlign: "center", pt: 0.75, pb: 2.25 }}>
    <Txt
      sx={{
        fontSize: 11.5,
        fontWeight: 600,
        color: SZ.ink500,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
      }}
    >
      {label}
    </Txt>
    <Txt
      sx={{
        fontFamily: SZ.mono,
        fontSize: 34,
        fontWeight: 600,
        color: SZ.teal700,
        letterSpacing: "-0.01em",
        mt: "2px",
        lineHeight: 1.1,
      }}
    >
      {value}
    </Txt>
    {sub && (
      <Txt sx={{ fontSize: 12.5, color: SZ.ink500, mt: "2px" }}>{sub}</Txt>
    )}
  </Box>
);

export const Kv = ({ k, v, tone }) => (
  <Flx
    jb
    sx={{
      alignItems: "baseline",
      py: "9px",
      borderTop: `1px solid ${SZ.line}`,
      "&:first-of-type": { borderTop: 0 },
    }}
  >
    <Txt sx={{ fontSize: 12.5, color: SZ.ink500 }}>{k}</Txt>
    <Txt
      sx={{
        fontFamily: SZ.mono,
        fontSize: 13,
        fontWeight: 500,
        color: tone === "pos" ? SZ.good : tone === "neg" ? SZ.bad : SZ.ink900,
      }}
    >
      {v}
    </Txt>
  </Flx>
);

export const Badge = ({ tone = "info", children }) => {
  const map = {
    pass: { bg: SZ.goodBg, fg: SZ.good },
    fail: { bg: SZ.badBg, fg: SZ.bad },
    info: { bg: SZ.teal50, fg: SZ.teal700 },
  };
  const s = map[tone] || map.info;
  return (
    <Box
      sx={{
        fontSize: 11.5,
        fontWeight: 600,
        padding: "4px 10px",
        borderRadius: "6px",
        background: s.bg,
        color: s.fg,
      }}
    >
      {children}
    </Box>
  );
};

export const SectionLabel = ({ children }) => (
  <Txt
    sx={{
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      color: SZ.ink400,
      mb: 0.5,
    }}
  >
    {children}
  </Txt>
);
